import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { generateSiteInductionsPDF } from '../utils/siteInductionsPDF';
import {
  Archive,
  Building2,
  Calendar,
  Users,
  ClipboardCheck,
  RotateCcw,
  Trash2,
  Search,
  Filter,
  Eye,
  AlertTriangle,
  Download,
  X,
  DollarSign,
  CheckCircle,
  Clock,
  Ban,
  FileText,
  Save,
  ChevronDown,
} from 'lucide-react';

type PaymentStatus = 'unpaid' | 'invoiced' | 'partially_paid' | 'paid' | 'written_off';

interface ArchivedTimesheet {
  id: string;
  worker_id: string;
  clock_in_time: string;
  clock_out_time: string | null;
  total_hours: number | null;
  status: string;
  regular_hours: number | null;
  overtime_hours: number | null;
  total_pay: number | null;
  is_amended: boolean;
  amended_note: string | null;
}

interface ArchivedSite {
  id: string;
  original_site_id: string;
  site_data: {
    id: string;
    name: string;
    type: string;
    address: string;
    start_date: string;
    end_date: string;
    description?: string;
    requirements?: string[];
    status: string;
    created_at: string;
    client_name?: string;
    client_address?: string;
    client_phone?: string;
    principal?: string;
  };
  project_status_data?: any;
  inductions_count: number;
  workers_count: number;
  archived_by?: string;
  archived_reason?: string;
  archived_at: string;
  original_created_at: string;
  timesheets_data?: ArchivedTimesheet[] | null;
  // Payment fields
  payment_status: PaymentStatus;
  invoice_number?: string;
  invoice_date?: string;
  invoice_amount?: number;
  payment_received?: number;
  payment_date?: string;
  payment_notes?: string;
}

const PAYMENT_STATUS_CONFIG: Record<PaymentStatus, { label: string; colour: string; bg: string; icon: React.FC<{ className?: string }> }> = {
  unpaid:        { label: 'Unpaid',         colour: 'text-red-700',    bg: 'bg-red-50 border-red-200',      icon: Clock },
  invoiced:      { label: 'Invoiced',       colour: 'text-blue-700',   bg: 'bg-blue-50 border-blue-200',    icon: FileText },
  partially_paid:{ label: 'Part Paid',      colour: 'text-amber-700',  bg: 'bg-amber-50 border-amber-200',  icon: DollarSign },
  paid:          { label: 'Paid',           colour: 'text-emerald-700',bg: 'bg-emerald-50 border-emerald-200', icon: CheckCircle },
  written_off:   { label: 'Written Off',    colour: 'text-gray-500',   bg: 'bg-gray-50 border-gray-200',    icon: Ban },
};

function fmt(n?: number | null): string {
  if (n == null) return '—';
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n);
}

function PaymentBadge({ status }: { status: PaymentStatus }) {
  const cfg = PAYMENT_STATUS_CONFIG[status] ?? PAYMENT_STATUS_CONFIG.unpaid;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.colour} ${cfg.bg}`}>
      <Icon className="h-3 w-3" />
      {cfg.label}
    </span>
  );
}

export const ArchivedSitesPage: React.FC = () => {
  const [archivedSites, setArchivedSites] = useState<ArchivedSite[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterPayment, setFilterPayment] = useState<'all' | PaymentStatus>('all');

  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedSite, setSelectedSite] = useState<ArchivedSite | null>(null);

  const [siteInductions, setSiteInductions] = useState<any[]>([]);
  const [loadingInductions, setLoadingInductions] = useState(false);
  const [archivedTimesheets, setArchivedTimesheets] = useState<any[]>([]);
  const [loadingTimesheets, setLoadingTimesheets] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Payment edit state
  const [paymentEdit, setPaymentEdit] = useState<{
    payment_status: PaymentStatus;
    invoice_number: string;
    invoice_date: string;
    invoice_amount: string;
    payment_received: string;
    payment_date: string;
    payment_notes: string;
  } | null>(null);
  const [savingPayment, setSavingPayment] = useState(false);

  useEffect(() => {
    fetchArchivedSites();
  }, []);

  const fetchArchivedSites = async () => {
    try {
      const { data, error } = await supabase
        .from('archived_sites')
        .select('*')
        .order('archived_at', { ascending: false });
      if (error) throw error;
      setArchivedSites(data || []);
    } catch (error) {
      console.error('Error fetching archived sites:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSiteInductions = async (siteId: string) => {
    setLoadingInductions(true);
    try {
      const { data, error } = await supabase
        .from('inductions')
        .select('*, workers!inner(id, name, role, email, certifications)')
        .eq('site_id', siteId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setSiteInductions(data || []);
      return data || [];
    } catch (error) {
      console.error('Error fetching site inductions:', error);
      setSiteInductions([]);
      return [];
    } finally {
      setLoadingInductions(false);
    }
  };

  const fetchArchivedTimesheets = async (originalSiteId: string) => {
    setLoadingTimesheets(true);
    try {
      const { data, error } = await supabase
        .from('timesheets')
        .select('id, worker_id, clock_in_time, clock_out_time, total_hours, status, regular_hours, overtime_hours, total_pay, is_amended, amended_note, worker:workers(name)')
        .eq('archived_site_id', originalSiteId)
        .order('clock_in_time', { ascending: false });
      if (error) throw error;
      setArchivedTimesheets(data || []);
    } catch (error) {
      console.error('Error fetching archived timesheets:', error);
      setArchivedTimesheets([]);
    } finally {
      setLoadingTimesheets(false);
    }
  };

  const handleViewDetails = (site: ArchivedSite) => {
    setSelectedSite(site);
    setPaymentEdit({
      payment_status: site.payment_status ?? 'unpaid',
      invoice_number: site.invoice_number ?? '',
      invoice_date: site.invoice_date ?? '',
      invoice_amount: site.invoice_amount != null ? String(site.invoice_amount) : '',
      payment_received: site.payment_received != null ? String(site.payment_received) : '',
      payment_date: site.payment_date ?? '',
      payment_notes: site.payment_notes ?? '',
    });
    setShowDetailModal(true);
    fetchSiteInductions(site.original_site_id);
    fetchArchivedTimesheets(site.original_site_id);
  };

  const handleSavePayment = async () => {
    if (!selectedSite || !paymentEdit) return;
    setSavingPayment(true);
    try {
      const updates = {
        payment_status: paymentEdit.payment_status,
        invoice_number: paymentEdit.invoice_number || null,
        invoice_date: paymentEdit.invoice_date || null,
        invoice_amount: paymentEdit.invoice_amount !== '' ? parseFloat(paymentEdit.invoice_amount) : null,
        payment_received: paymentEdit.payment_received !== '' ? parseFloat(paymentEdit.payment_received) : null,
        payment_date: paymentEdit.payment_date || null,
        payment_notes: paymentEdit.payment_notes || null,
      };
      const { error } = await supabase
        .from('archived_sites')
        .update(updates)
        .eq('id', selectedSite.id);
      if (error) throw error;
      setArchivedSites(prev =>
        prev.map(s => s.id === selectedSite.id ? { ...s, ...updates } : s)
      );
      setSelectedSite(prev => prev ? { ...prev, ...updates } : null);
    } catch (error) {
      console.error('Error saving payment:', error);
      alert('Failed to save payment details. Please try again.');
    } finally {
      setSavingPayment(false);
    }
  };

  const handleDownloadInductions = (site: ArchivedSite, inductionsData?: any[]) => {
    const inductions = inductionsData || siteInductions;
    const completedInductions = inductions.filter(i => i.status === 'completed');
    if (completedInductions.length === 0) {
      alert('No completed inductions found for this archived site.');
      return;
    }
    generateSiteInductionsPDF({
      site: site.site_data,
      inductions: completedInductions.map(i => ({ ...i, worker: i.workers })),
      completedCount: completedInductions.length,
      pendingCount: inductions.filter(i => i.status === 'pending').length,
      totalCount: inductions.length,
    });
  };

  const handleRestore = async () => {
    if (!selectedSite) return;
    setRestoring(true);
    try {
      const { error: rpcError } = await supabase
        .rpc('restore_archived_site', { archived_site_id_param: selectedSite.id });
      if (rpcError) throw rpcError;

      setShowRestoreModal(false);
      setShowDetailModal(false);
      setSelectedSite(null);
      fetchArchivedSites();
    } catch (error) {
      console.error('Error restoring site:', error);
      alert('Failed to restore site. Please try again.');
    } finally {
      setRestoring(false);
    }
  };

  const handlePermanentDelete = async () => {
    if (!selectedSite) return;
    setDeleting(true);
    try {
      const { error } = await supabase
        .from('archived_sites')
        .delete()
        .eq('id', selectedSite.id);
      if (error) throw error;
      setShowDeleteModal(false);
      setShowDetailModal(false);
      setSelectedSite(null);
      fetchArchivedSites();
    } catch (error) {
      console.error('Error permanently deleting site:', error);
      alert('Failed to delete site. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const filteredSites = archivedSites.filter(site => {
    const matchesSearch =
      site.site_data.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      site.site_data.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (site.site_data.client_name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || site.site_data.type === filterType;
    const matchesPayment = filterPayment === 'all' || site.payment_status === filterPayment;
    return matchesSearch && matchesType && matchesPayment;
  });

  // Summary totals
  const totalInvoiced = archivedSites.reduce((s, x) => s + (x.invoice_amount ?? 0), 0);
  const totalReceived = archivedSites.reduce((s, x) => s + (x.payment_received ?? 0), 0);
  const totalOutstanding = archivedSites
    .filter(x => x.payment_status !== 'paid' && x.payment_status !== 'written_off')
    .reduce((s, x) => s + Math.max(0, (x.invoice_amount ?? 0) - (x.payment_received ?? 0)), 0);
  const countByStatus = (s: PaymentStatus) => archivedSites.filter(x => x.payment_status === s).length;

  const getStatusColour = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'suspended': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Archived Sites</h1>
          <p className="text-sm text-gray-600 mt-1">
            {archivedSites.length} archived sites — track payment status and reconcile invoices
          </p>
        </div>
        <Link
          to="/sites"
          className="bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-orange-700 transition-colors"
        >
          <Building2 className="h-5 w-5" />
          <span>Active Sites</span>
        </Link>
      </div>

      {/* Payment Summary Bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Total Invoiced</p>
          <p className="text-xl font-bold text-gray-900">{fmt(totalInvoiced)}</p>
          <p className="text-xs text-gray-400 mt-0.5">{archivedSites.filter(x => x.invoice_amount != null).length} sites invoiced</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Total Received</p>
          <p className="text-xl font-bold text-emerald-600">{fmt(totalReceived)}</p>
          <p className="text-xs text-gray-400 mt-0.5">{countByStatus('paid')} fully paid</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Outstanding</p>
          <p className={`text-xl font-bold ${totalOutstanding > 0 ? 'text-red-600' : 'text-gray-400'}`}>{fmt(totalOutstanding)}</p>
          <p className="text-xs text-gray-400 mt-0.5">{countByStatus('unpaid')} unpaid · {countByStatus('invoiced')} invoiced · {countByStatus('partially_paid')} part paid</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Written Off</p>
          <p className="text-xl font-bold text-gray-500">
            {fmt(archivedSites.filter(x => x.payment_status === 'written_off').reduce((s, x) => s + (x.invoice_amount ?? 0), 0))}
          </p>
          <p className="text-xs text-gray-400 mt-0.5">{countByStatus('written_off')} sites</p>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by site name, address, or client..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <select
              value={filterType}
              onChange={e => setFilterType(e.target.value)}
              className="pl-10 pr-8 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="all">All Types</option>
              <option value="demolition">Demolition</option>
              <option value="asbestos_removal">Asbestos Removal</option>
              <option value="renovation">Renovation</option>
              <option value="construction">Construction</option>
            </select>
          </div>
          <div className="relative">
            <DollarSign className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <select
              value={filterPayment}
              onChange={e => setFilterPayment(e.target.value as any)}
              className="pl-10 pr-8 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="all">All Payment Status</option>
              <option value="unpaid">Unpaid</option>
              <option value="invoiced">Invoiced</option>
              <option value="partially_paid">Part Paid</option>
              <option value="paid">Paid</option>
              <option value="written_off">Written Off</option>
            </select>
          </div>
        </div>
      </div>

      {/* Archived Sites Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredSites.map(archivedSite => {
          const outstanding = Math.max(0, (archivedSite.invoice_amount ?? 0) - (archivedSite.payment_received ?? 0));
          return (
            <div
              key={archivedSite.id}
              className="relative bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
            >
              {/* Payment status accent stripe */}
              <div className={`h-1 ${
                archivedSite.payment_status === 'paid' ? 'bg-emerald-500' :
                archivedSite.payment_status === 'invoiced' ? 'bg-blue-500' :
                archivedSite.payment_status === 'partially_paid' ? 'bg-amber-400' :
                archivedSite.payment_status === 'written_off' ? 'bg-gray-300' :
                'bg-red-400'
              }`} />

              {/* PAID stamp overlay */}
              {archivedSite.payment_status === 'paid' && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                  <div
                    className="rotate-[-32deg] select-none"
                    style={{
                      border: '4px solid #059669',
                      color: '#059669',
                      fontSize: '2.4rem',
                      fontFamily: 'Georgia, serif',
                      fontWeight: 900,
                      letterSpacing: '0.35em',
                      padding: '6px 20px',
                      borderRadius: '6px',
                      opacity: 0.65,
                      textShadow: '0 0 1px #059669',
                      boxShadow: 'inset 0 0 0 2px rgba(5,150,105,0.15)',
                    }}
                  >
                    PAID
                  </div>
                </div>
              )}

              <div className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3 min-w-0">
                    <div className="h-9 w-9 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Archive className="h-4 w-4 text-gray-600" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-base font-semibold text-gray-900 truncate">{archivedSite.site_data.name}</h3>
                      <p className="text-xs text-gray-500 truncate">{archivedSite.site_data.address}</p>
                    </div>
                  </div>
                  <PaymentBadge status={archivedSite.payment_status} />
                </div>

                {archivedSite.site_data.client_name && (
                  <p className="text-xs text-gray-500 mb-3">
                    <span className="font-medium">Client:</span> {archivedSite.site_data.client_name}
                  </p>
                )}

                {/* Financial row */}
                {(archivedSite.invoice_amount != null || archivedSite.payment_received != null) && (
                  <div className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 mb-3 text-sm">
                    <div>
                      <p className="text-xs text-gray-500">Invoiced</p>
                      <p className="font-semibold text-gray-900">{fmt(archivedSite.invoice_amount)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-500">Received</p>
                      <p className="font-semibold text-emerald-600">{fmt(archivedSite.payment_received)}</p>
                    </div>
                    {outstanding > 0 && (
                      <div className="text-right">
                        <p className="text-xs text-gray-500">Outstanding</p>
                        <p className="font-semibold text-red-600">{fmt(outstanding)}</p>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    Archived {new Date(archivedSite.archived_at).toLocaleDateString()}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="flex items-center gap-1">
                      <ClipboardCheck className="h-3.5 w-3.5 text-green-600" />
                      {archivedSite.inductions_count}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="h-3.5 w-3.5 text-blue-600" />
                      {archivedSite.workers_count}
                    </span>
                  </div>
                </div>

                {archivedSite.archived_reason && (
                  <div className="bg-gray-50 rounded-lg px-3 py-2 mb-3">
                    <p className="text-xs text-gray-500 italic truncate">{archivedSite.archived_reason}</p>
                  </div>
                )}

                <div className="flex gap-2">
                  <button
                    onClick={() => handleViewDetails(archivedSite)}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium"
                  >
                    <Eye className="h-4 w-4" />
                    View &amp; Reconcile
                  </button>
                  {archivedSite.inductions_count > 0 && (
                    <button
                      onClick={() => {
                        fetchSiteInductions(archivedSite.original_site_id).then(data =>
                          handleDownloadInductions(archivedSite, data)
                        );
                      }}
                      className="flex items-center justify-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      title="Download Inductions Report"
                    >
                      <Download className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredSites.length === 0 && (
        <div className="text-center py-16">
          <Archive className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">
            {searchTerm || filterType !== 'all' || filterPayment !== 'all'
              ? 'No matching archived sites'
              : 'No archived sites'}
          </h3>
          <p className="text-sm text-gray-400">
            {searchTerm || filterType !== 'all' || filterPayment !== 'all'
              ? 'Try adjusting your search or filter criteria.'
              : 'Sites that are archived will appear here.'}
          </p>
          {(searchTerm || filterType !== 'all' || filterPayment !== 'all') && (
            <button
              onClick={() => { setSearchTerm(''); setFilterType('all'); setFilterPayment('all'); }}
              className="mt-3 text-orange-600 hover:text-orange-700 text-sm font-medium"
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* ─── Detail / Reconciliation Modal ─── */}
      {showDetailModal && selectedSite && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[92vh] overflow-y-auto">
            {/* Modal header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Archive className="h-5 w-5 text-gray-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{selectedSite.site_data.name}</h3>
                  <p className="text-sm text-gray-500">{selectedSite.site_data.address}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setSelectedSite(s => s ? { ...s } : null); setShowRestoreModal(true); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <RotateCcw className="h-4 w-4" />
                  Restore
                </button>
                <button
                  onClick={() => { setSelectedSite(s => s ? { ...s } : null); setShowDeleteModal(true); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
                <button
                  onClick={() => { setShowDetailModal(false); setSelectedSite(null); setSiteInductions([]); }}
                  className="p-1.5 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* ── Payment Reconciliation ── */}
              <div className="bg-white rounded-xl border-2 border-orange-100 shadow-sm overflow-hidden">
                <div className="flex items-center gap-2 px-5 py-3 bg-orange-50 border-b border-orange-100">
                  <DollarSign className="h-5 w-5 text-orange-600" />
                  <h4 className="font-semibold text-gray-900">Payment Reconciliation</h4>
                  {selectedSite.payment_status && (
                    <div className="ml-auto">
                      <PaymentBadge status={selectedSite.payment_status} />
                    </div>
                  )}
                </div>

                {paymentEdit && (
                  <div className="p-5 space-y-4">
                    {/* Status */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Payment Status</label>
                        <select
                          value={paymentEdit.payment_status}
                          onChange={e => setPaymentEdit(p => p ? { ...p, payment_status: e.target.value as PaymentStatus } : null)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                        >
                          <option value="unpaid">Unpaid</option>
                          <option value="invoiced">Invoiced</option>
                          <option value="partially_paid">Partially Paid</option>
                          <option value="paid">Paid</option>
                          <option value="written_off">Written Off</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Invoice Number</label>
                        <input
                          type="text"
                          value={paymentEdit.invoice_number}
                          onChange={e => setPaymentEdit(p => p ? { ...p, invoice_number: e.target.value } : null)}
                          placeholder="e.g. INV-0042"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Invoice Date</label>
                        <input
                          type="date"
                          value={paymentEdit.invoice_date}
                          onChange={e => setPaymentEdit(p => p ? { ...p, invoice_date: e.target.value } : null)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Invoice Amount ($)</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={paymentEdit.invoice_amount}
                          onChange={e => setPaymentEdit(p => p ? { ...p, invoice_amount: e.target.value } : null)}
                          placeholder="0.00"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Amount Received ($)</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={paymentEdit.payment_received}
                          onChange={e => setPaymentEdit(p => p ? { ...p, payment_received: e.target.value } : null)}
                          placeholder="0.00"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Payment Date</label>
                        <input
                          type="date"
                          value={paymentEdit.payment_date}
                          onChange={e => setPaymentEdit(p => p ? { ...p, payment_date: e.target.value } : null)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                        <input
                          type="text"
                          value={paymentEdit.payment_notes}
                          onChange={e => setPaymentEdit(p => p ? { ...p, payment_notes: e.target.value } : null)}
                          placeholder="e.g. Paid via EFT ref #1234"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                        />
                      </div>
                    </div>

                    {/* Outstanding callout */}
                    {paymentEdit.invoice_amount !== '' && paymentEdit.payment_received !== '' && (
                      (() => {
                        const outstanding = parseFloat(paymentEdit.invoice_amount || '0') - parseFloat(paymentEdit.payment_received || '0');
                        return outstanding > 0 ? (
                          <div className="flex items-center justify-between bg-red-50 border border-red-200 rounded-lg px-4 py-2.5">
                            <span className="text-sm font-medium text-red-700">Outstanding balance</span>
                            <span className="text-lg font-bold text-red-700">{fmt(outstanding)}</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2.5">
                            <CheckCircle className="h-4 w-4 text-emerald-600" />
                            <span className="text-sm font-medium text-emerald-700">Fully paid — no outstanding balance</span>
                          </div>
                        );
                      })()
                    )}

                    <div className="flex justify-end pt-1">
                      <button
                        onClick={handleSavePayment}
                        disabled={savingPayment}
                        className="flex items-center gap-1.5 px-5 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 transition-colors text-sm font-medium"
                      >
                        <Save className="h-4 w-4" />
                        {savingPayment ? 'Saving...' : 'Save Payment Details'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* ── Site Information ── */}
              <div className="bg-gray-50 rounded-xl p-5">
                <h4 className="font-semibold text-gray-900 mb-4">Site Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-0.5">Site Type</p>
                    <p className="text-gray-900 capitalize">{selectedSite.site_data.type.replace(/_/g, ' ')}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-0.5">Status when archived</p>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColour(selectedSite.site_data.status)}`}>
                      {selectedSite.site_data.status.charAt(0).toUpperCase() + selectedSite.site_data.status.slice(1)}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-0.5">Project Duration</p>
                    <p className="text-gray-900">
                      {new Date(selectedSite.site_data.start_date).toLocaleDateString()} – {new Date(selectedSite.site_data.end_date).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-0.5">Archived</p>
                    <p className="text-gray-900">
                      {new Date(selectedSite.archived_at).toLocaleDateString()}
                      {selectedSite.archived_reason && ` — ${selectedSite.archived_reason}`}
                    </p>
                  </div>
                  {selectedSite.site_data.client_name && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-0.5">Client</p>
                      <p className="text-gray-900">{selectedSite.site_data.client_name}</p>
                      {selectedSite.site_data.client_phone && <p className="text-gray-500 text-xs">{selectedSite.site_data.client_phone}</p>}
                      {selectedSite.site_data.client_address && <p className="text-gray-500 text-xs">{selectedSite.site_data.client_address}</p>}
                    </div>
                  )}
                  {selectedSite.site_data.principal && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-0.5">Principal Contractor</p>
                      <p className="text-gray-900">{selectedSite.site_data.principal}</p>
                    </div>
                  )}
                </div>

                {selectedSite.site_data.description && (
                  <div className="mt-4">
                    <p className="text-xs font-medium text-gray-500 mb-1">Description</p>
                    <p className="text-sm text-gray-900">{selectedSite.site_data.description}</p>
                  </div>
                )}

                {selectedSite.site_data.requirements && selectedSite.site_data.requirements.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs font-medium text-gray-500 mb-2">Safety Requirements</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedSite.site_data.requirements.map((req, i) => (
                        <span key={i} className="bg-orange-100 text-orange-800 px-2 py-0.5 rounded text-xs">{req}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* ── Project Status ── */}
              {selectedSite.project_status_data && (
                <div className="bg-gray-50 rounded-xl p-5">
                  <h4 className="font-semibold text-gray-900 mb-4">Project Status (when archived)</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    {[
                      ['Gas Approval', selectedSite.project_status_data.gas_approval],
                      ['Electrical', selectedSite.project_status_data.electrical_approval],
                      ['Council', selectedSite.project_status_data.council_approval],
                      ['EPA Permit', selectedSite.project_status_data.epa_permit],
                    ].map(([label, value]) => value && (
                      <div key={label}>
                        <p className="text-xs font-medium text-gray-500 mb-0.5">{label}</p>
                        <p className="text-gray-900 capitalize">{String(value).replace(/_/g, ' ')}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Timesheets Summary ── */}
              <div className="bg-gray-50 rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-gray-900">Timesheets (Clock In/Out)</h4>
                  <span className="text-xs text-gray-500">
                    {loadingTimesheets ? 'Loading...' : `${archivedTimesheets.length} records`}
                  </span>
                </div>
                {loadingTimesheets ? (
                  <div className="flex items-center justify-center py-4 gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500" />
                    <span className="text-sm text-gray-500">Loading...</span>
                  </div>
                ) : archivedTimesheets.length > 0 ? (
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {archivedTimesheets.map(ts => {
                      const workerName = ts.worker?.name || 'Unknown worker';
                      const clockIn = new Date(ts.clock_in_time);
                      const clockOut = ts.clock_out_time ? new Date(ts.clock_out_time) : null;
                      return (
                        <div key={ts.id} className="bg-white rounded-lg border border-gray-200 px-3 py-2.5">
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-sm font-medium text-gray-900">{workerName}</p>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              ts.status === 'completed' ? 'bg-green-100 text-green-800' :
                              ts.status === 'active' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {ts.status.charAt(0).toUpperCase() + ts.status.slice(1)}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <span>In: {clockIn.toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                            <span>Out: {clockOut ? clockOut.toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}</span>
                            <span>Hours: {ts.total_hours != null ? Number(ts.total_hours).toFixed(2) : '—'}</span>
                            {ts.total_pay != null && <span>Pay: {fmt(Number(ts.total_pay))}</span>}
                          </div>
                          {ts.is_amended && ts.amended_note && (
                            <p className="text-xs text-amber-600 mt-1">Amended: {ts.amended_note}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : selectedSite.timesheets_data && selectedSite.timesheets_data.length > 0 ? (
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {selectedSite.timesheets_data.map(ts => {
                      const worker = siteInductions.find(i => i.worker_id === ts.worker_id)?.workers;
                      const workerName = worker?.name || 'Unknown worker';
                      const clockIn = new Date(ts.clock_in_time);
                      const clockOut = ts.clock_out_time ? new Date(ts.clock_out_time) : null;
                      return (
                        <div key={ts.id} className="bg-white rounded-lg border border-gray-200 px-3 py-2.5">
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-sm font-medium text-gray-900">{workerName}</p>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              ts.status === 'completed' ? 'bg-green-100 text-green-800' :
                              ts.status === 'active' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {ts.status.charAt(0).toUpperCase() + ts.status.slice(1)}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <span>In: {clockIn.toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                            <span>Out: {clockOut ? clockOut.toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}</span>
                            <span>Hours: {ts.total_hours != null ? Number(ts.total_hours).toFixed(2) : '—'}</span>
                            {ts.total_pay != null && <span>Pay: {fmt(Number(ts.total_pay))}</span>}
                          </div>
                          {ts.is_amended && ts.amended_note && (
                            <p className="text-xs text-amber-600 mt-1">Amended: {ts.amended_note}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 text-center py-4">No timesheet records found.</p>
                )}
              </div>

              {/* ── Inductions Summary ── */}
              <div className="bg-gray-50 rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-gray-900">Inductions</h4>
                  {siteInductions.filter(i => i.status === 'completed').length > 0 && (
                    <button
                      onClick={() => handleDownloadInductions(selectedSite)}
                      className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                    >
                      <Download className="h-4 w-4" />
                      Download Report
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-4 mb-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{selectedSite.inductions_count}</p>
                    <p className="text-xs text-gray-500">Total</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-emerald-600">{siteInductions.filter(i => i.status === 'completed').length}</p>
                    <p className="text-xs text-gray-500">Completed</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-amber-600">{siteInductions.filter(i => i.status === 'pending').length}</p>
                    <p className="text-xs text-gray-500">Pending</p>
                  </div>
                </div>

                {loadingInductions ? (
                  <div className="flex items-center justify-center py-4 gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500" />
                    <span className="text-sm text-gray-500">Loading...</span>
                  </div>
                ) : siteInductions.length > 0 ? (
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {siteInductions.map(induction => (
                      <div key={induction.id} className="bg-white rounded-lg border border-gray-200 px-3 py-2 flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{induction.workers?.name}</p>
                          <p className="text-xs text-gray-400 capitalize">{induction.workers?.role?.replace(/_/g, ' ')}</p>
                        </div>
                        <div className="text-right">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            induction.status === 'completed' ? 'bg-green-100 text-green-800' :
                            induction.status === 'pending' ? 'bg-amber-100 text-amber-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {induction.status.charAt(0).toUpperCase() + induction.status.slice(1)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 text-center py-4">No induction records found.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Restore Modal ─── */}
      {showRestoreModal && selectedSite && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="flex items-center gap-3 p-6 border-b border-gray-200">
              <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center">
                <RotateCcw className="h-5 w-5 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Restore Site</h3>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">
                Restore <strong>{selectedSite.site_data.name}</strong> to the active sites list?
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-xs text-blue-800">
                  The site will be restored with "Active" status. Timesheet, induction, and worker data will be re-linked to the restored site.
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowRestoreModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRestore}
                  disabled={restoring}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors text-sm font-medium"
                >
                  {restoring ? 'Restoring...' : 'Restore Site'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Delete Modal ─── */}
      {showDeleteModal && selectedSite && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="flex items-center gap-3 p-6 border-b border-gray-200">
              <div className="h-10 w-10 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Permanently Delete</h3>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">
                Permanently delete <strong>{selectedSite.site_data.name}</strong>? This cannot be undone.
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <p className="text-xs text-red-800">
                  All archived data including payment records for this site will be permanently removed.
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePermanentDelete}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors text-sm font-medium"
                >
                  {deleting ? 'Deleting...' : 'Delete Permanently'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
